import { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";

const API_BASE =
  "https://www.sevaafoundation.com/seva-backend/api";

type DonorDetails = {
  name: string;
  email: string;
  phone: string;
  pan?: string;
  state?: string;
  district?: string;
  addressLine?: string;
  campaign_id?: string;
};

type CheckoutState = {
  amount: number;
  amountPaise: number;
  currency: string;
  donorDetails: DonorDetails;
  donationId: number;
  orderId: string;
  keyId: string;
};

type RazorpaySuccessResponse = {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
};

type RazorpayFailureResponse = {
  error?: {
    code?: string;
    description?: string;
    source?: string;
    step?: string;
    reason?: string;
    metadata?: {
      order_id?: string;
      payment_id?: string;
    };
  };
};

type RazorpayOptions = {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  image: string;
  order_id: string;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  notes: Record<string, string>;
  theme: {
    color: string;
  };
  handler: (
    response: RazorpaySuccessResponse
  ) => void | Promise<void>;
  modal: {
    ondismiss: () => void;
    confirm_close: boolean;
  };
};

type RazorpayInstance = {
  open: () => void;
  on: (
    event: "payment.failed",
    callback: (
      response: RazorpayFailureResponse
    ) => void
  ) => void;
};

declare global {
  interface Window {
    Razorpay?: new (
      options: RazorpayOptions
    ) => RazorpayInstance;
  }
}

const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );

    if (existingScript) {
      existingScript.addEventListener(
        "load",
        () => resolve(true),
        { once: true }
      );
      existingScript.addEventListener(
        "error",
        () => resolve(false),
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const checkoutState =
    location.state as CheckoutState | null;

  const [isScriptReady, setIsScriptReady] =
    useState(false);
  const [isProcessing, setIsProcessing] =
    useState(false);
  const [message, setMessage] = useState("");

  const isValidState = useMemo(() => {
    return Boolean(
      checkoutState?.donationId &&
        checkoutState?.orderId &&
        checkoutState?.keyId &&
        checkoutState?.amountPaise > 0 &&
        checkoutState?.donorDetails?.email
    );
  }, [checkoutState]);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!isValidState) {
      return;
    }

    loadRazorpayScript().then((loaded) => {
      setIsScriptReady(loaded);

      if (!loaded) {
        setMessage(
          "Unable to load Razorpay Checkout. Please check your internet connection."
        );
      }
    });
  }, [isValidState]);

  const verifyPayment = async (
    paymentResponse: RazorpaySuccessResponse
  ) => {
    if (!checkoutState) return;

    setIsProcessing(true);
    setMessage("Verifying your payment securely...");

    try {
      const response = await fetch(
        `${API_BASE}/donations/verify.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            donationId: checkoutState.donationId,
            razorpay_order_id:
              paymentResponse.razorpay_order_id,
            razorpay_payment_id:
              paymentResponse.razorpay_payment_id,
            razorpay_signature:
              paymentResponse.razorpay_signature,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error ||
            "Payment verification failed."
        );
      }

      localStorage.setItem(
        "sevaa_user",
        JSON.stringify({
          ...checkoutState.donorDetails,
          address:
            checkoutState.donorDetails.addressLine ||
            "",
        })
      );

      navigate("/donor-profile?tab=history", {
        replace: true,
        state: {
          paymentSuccess: true,
          receiptNo: data.receiptNo,
          paymentId: data.paymentId,
        },
      });
    } catch (error) {
      console.error(
        "Razorpay payment verification failed:",
        error
      );

      setMessage(
        error instanceof Error
          ? error.message
          : "Payment verification failed. Please contact support with your payment ID."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const openRazorpay = async () => {
    if (!checkoutState || !isValidState) {
      navigate("/donate", { replace: true });
      return;
    }

    setMessage("");

    const loaded =
      isScriptReady || (await loadRazorpayScript());

    if (!loaded || !window.Razorpay) {
      setMessage(
        "Razorpay Checkout could not be loaded."
      );
      return;
    }

    const options: RazorpayOptions = {
      key: checkoutState.keyId,
      amount: checkoutState.amountPaise,
      currency: checkoutState.currency || "INR",
      name: "SEVAA Foundation",
      description: "Secure NGO Donation",
      image:
        "https://www.sevaafoundation.com/logo512.png",
      order_id: checkoutState.orderId,

      prefill: {
        name: checkoutState.donorDetails.name,
        email: checkoutState.donorDetails.email,
        contact: checkoutState.donorDetails.phone,
      },

      notes: {
        donation_id:
          checkoutState.donationId.toString(),
        campaign_id:
          checkoutState.donorDetails.campaign_id ||
          "general",
      },

      theme: {
        color: "#0f766e",
      },

      handler: verifyPayment,

      modal: {
        confirm_close: true,
        ondismiss: () => {
          setMessage(
            "Payment window was closed. You can try again."
          );
        },
      },
    };

    const razorpay = new window.Razorpay(options);

    razorpay.on("payment.failed", (response) => {
      const description =
        response.error?.description ||
        "Payment failed. Please try another payment method.";

      setMessage(description);
    });

    razorpay.open();
  };

  if (!isValidState || !checkoutState) {
    return (
      <main className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <section className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 shadow-xl p-8 text-center">
          <h1 className="text-2xl font-black text-slate-900">
            Donation Session Not Found
          </h1>

          <p className="text-slate-500 mt-3">
            Please reopen the donation form and create
            a fresh secure payment order.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/donate", {
                replace: true,
              })
            }
            className="mt-6 bg-[#0f766e] text-white px-6 py-3 rounded-xl font-black"
          >
            Go to Donate Page
          </button>
        </section>
      </main>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Secure Donation Checkout | SEVAA Foundation
        </title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="min-h-screen bg-slate-50 py-12 px-4 font-poppins">
        <div className="max-w-4xl mx-auto">
          <section className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden grid lg:grid-cols-2">
            <div className="bg-[#0f766e] text-white p-8 md:p-10">
              <span className="inline-flex bg-white/15 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                Razorpay Test Mode
              </span>

              <h1 className="text-3xl font-black mt-5">
                Complete Your Donation
              </h1>

              <p className="text-teal-100 mt-3 leading-relaxed">
                Your payment is processed securely by
                Razorpay. SEVAA Foundation does not store
                your card, UPI PIN or banking credentials.
              </p>

              <div className="mt-8 space-y-4">
                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-teal-100 font-bold">
                    Donor
                  </p>
                  <p className="font-black mt-1">
                    {checkoutState.donorDetails.name}
                  </p>
                  <p className="text-sm text-teal-100">
                    {checkoutState.donorDetails.email}
                  </p>
                </div>

                <div className="bg-white/10 rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-widest text-teal-100 font-bold">
                    Razorpay Order
                  </p>
                  <p className="font-mono text-sm break-all mt-1">
                    {checkoutState.orderId}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 md:p-10 flex flex-col justify-center">
              <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">
                Donation Amount
              </p>

              <p className="text-5xl font-black text-slate-900 mt-2">
                ₹
                {checkoutState.amount.toLocaleString(
                  "en-IN",
                  {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  }
                )}
              </p>

              <div className="mt-8 space-y-3 text-sm text-slate-600">
                <p className="flex justify-between border-b border-slate-100 pb-3">
                  <span>Payment gateway</span>
                  <strong>Razorpay</strong>
                </p>

                <p className="flex justify-between border-b border-slate-100 pb-3">
                  <span>Currency</span>
                  <strong>
                    {checkoutState.currency}
                  </strong>
                </p>

                <p className="flex justify-between">
                  <span>Status</span>
                  <strong className="text-orange-600">
                    Awaiting Payment
                  </strong>
                </p>
              </div>

              {message && (
                <div
                  className="mt-6 bg-orange-50 border border-orange-200 text-orange-800 rounded-xl p-4 text-sm font-semibold"
                  role="alert"
                >
                  {message}
                </div>
              )}

              <button
                type="button"
                onClick={openRazorpay}
                disabled={
                  isProcessing || !isScriptReady
                }
                className="mt-8 w-full bg-[#ffb000] hover:bg-[#e6a000] text-slate-900 py-4 rounded-xl font-black uppercase tracking-widest disabled:opacity-60"
              >
                {isProcessing
                  ? "Verifying Payment..."
                  : isScriptReady
                  ? `Pay ₹${checkoutState.amount.toLocaleString(
                      "en-IN"
                    )} Securely`
                  : "Loading Razorpay..."}
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate("/donate", {
                    replace: true,
                  })
                }
                disabled={isProcessing}
                className="mt-3 text-sm font-bold text-slate-500 hover:text-slate-900"
              >
                Cancel and return
              </button>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default Checkout;
